import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * Per-table column-config preferences hook (Sprint 12b).
 *
 * Drives three behaviors:
 *   - Click-header sort (sortKey + sortDir)
 *   - Drag-reorder columns (order)
 *   - Show/hide columns (hidden set)
 *
 * Persists per-storage-key into localStorage so user preferences
 * survive reloads. Use `quote-line-editor` style prefixes per consumer.
 *
 * Usage:
 *   const cfg = useColumnConfig({
 *     storageKey: 'quote-line-editor',
 *     columns: [
 *       { key: 'product', label: 'Product', sticky: true },
 *       { key: 'qty', label: 'Qty', sortable: true },
 *       ...
 *     ],
 *   })
 *   cfg.visibleColumns  // ColumnDef[] in user-defined order, hidden filtered
 *   cfg.toggleSort('qty')  // qty asc → desc → off
 *   cfg.setHidden('last_paid', true)  // hide a column
 *   cfg.setOrder(['qty', 'product', ...])  // user-driven reorder
 */

export type SortDir = 'asc' | 'desc'

export interface ColumnDef {
  /** Stable id used in storage. Avoid changing for shipped tables. */
  key: string
  /** Display label rendered in the header. */
  label: string
  /** When false, the click-header sort is disabled for this column. */
  sortable?: boolean
  /** When true, ColumnConfigMenu cannot hide nor reorder this column. */
  sticky?: boolean
  /** Suggested header cell width — passed through for the consumer to apply. */
  width?: number | string
  /** Optional alignment hint, consumer-defined. */
  align?: 'left' | 'right' | 'center'
}

export interface UseColumnConfigOptions {
  storageKey: string
  columns: ReadonlyArray<ColumnDef>
  /** Initial sort. Persisted overrides win on subsequent mounts. */
  defaultSort?: { key: string; dir: SortDir }
}

interface PersistedState {
  order: string[]
  hidden: string[]
  sortKey: string | null
  sortDir: SortDir
}

function _readPersisted(storageKey: string): Partial<PersistedState> | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(`column-config:${storageKey}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function _writePersisted(storageKey: string, state: PersistedState): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(
      `column-config:${storageKey}`,
      JSON.stringify(state),
    )
  } catch {
    // localStorage quota or private mode — silently skip persistence.
  }
}

export interface UseColumnConfigResult {
  /** All columns the consumer registered (definition only). */
  columns: ReadonlyArray<ColumnDef>
  /** Columns in user-defined order, with hidden filtered out. */
  visibleColumns: ColumnDef[]
  /** Set of hidden column keys. */
  hidden: ReadonlySet<string>
  /** Current sort: { key, dir } or null when no sort. */
  sortKey: string | null
  sortDir: SortDir
  /** Tri-state toggle: column key → asc → desc → off. */
  toggleSort: (key: string) => void
  /** Hide / show a single column. Sticky columns are no-ops. */
  setHidden: (key: string, hidden: boolean) => void
  /** Reset everything to definition order, no hidden, no sort. */
  reset: () => void
  /** Replace the column ordering. Sticky columns are kept fixed at the front. */
  setOrder: (keys: string[]) => void
  /** Sort a generic row list using the current sort key/dir. Caller
   *  provides a `valueFor(row, key)` accessor; rows return null/undefined
   *  for missing values and float to the bottom regardless of dir. */
  sortRows: <Row>(
    rows: Row[],
    valueFor: (row: Row, key: string) => unknown,
  ) => Row[]
}

export function useColumnConfig(
  opts: UseColumnConfigOptions,
): UseColumnConfigResult {
  const { storageKey, columns, defaultSort } = opts

  // Hydrate once from localStorage on mount; subsequent renders own their own
  // state and persist on change.
  const [order, _setOrder] = useState<string[]>(() => {
    const persisted = _readPersisted(storageKey)
    const fromStore = persisted?.order ?? []
    const known = new Set(columns.map((c) => c.key))
    // Drop columns from persisted order that no longer exist in the
    // definition; append columns that exist in the definition but
    // weren't in the persisted order (newly added). Sticky stays first.
    const keep = fromStore.filter((k) => known.has(k))
    const append = columns.map((c) => c.key).filter((k) => !keep.includes(k))
    const merged = [...keep, ...append]
    return _withStickyFirst(columns, merged)
  })

  const [hidden, _setHiddenSet] = useState<Set<string>>(() => {
    const persisted = _readPersisted(storageKey)
    const stickyKeys = new Set(
      columns.filter((c) => c.sticky).map((c) => c.key),
    )
    return new Set((persisted?.hidden ?? []).filter((k) => !stickyKeys.has(k)))
  })

  const [sortKey, _setSortKey] = useState<string | null>(() => {
    const persisted = _readPersisted(storageKey)
    if (persisted?.sortKey !== undefined) return persisted.sortKey
    return defaultSort?.key ?? null
  })

  const [sortDir, _setSortDir] = useState<SortDir>(() => {
    const persisted = _readPersisted(storageKey)
    if (persisted?.sortDir) return persisted.sortDir
    return defaultSort?.dir ?? 'asc'
  })

  // Persist on change.
  useEffect(() => {
    _writePersisted(storageKey, {
      order,
      hidden: Array.from(hidden),
      sortKey,
      sortDir,
    })
  }, [storageKey, order, hidden, sortKey, sortDir])

  const visibleColumns = useMemo<ColumnDef[]>(() => {
    const lookup = new Map(columns.map((c) => [c.key, c] as const))
    return order
      .map((k) => lookup.get(k))
      .filter((c): c is ColumnDef => Boolean(c))
      .filter((c) => !hidden.has(c.key))
  }, [order, hidden, columns])

  const toggleSort = useCallback((key: string) => {
    const def = columns.find((c) => c.key === key)
    if (!def || def.sortable === false) return
    if (sortKey !== key) {
      _setSortKey(key)
      _setSortDir('asc')
      return
    }
    if (sortDir === 'asc') {
      _setSortDir('desc')
      return
    }
    _setSortKey(null)
    _setSortDir('asc')
  }, [sortKey, sortDir, columns])

  const setHidden = useCallback((key: string, next: boolean) => {
    const def = columns.find((c) => c.key === key)
    if (!def || def.sticky) return
    _setHiddenSet((prev) => {
      const copy = new Set(prev)
      if (next) copy.add(key)
      else copy.delete(key)
      return copy
    })
  }, [columns])

  const setOrder = useCallback((keys: string[]) => {
    _setOrder(_withStickyFirst(columns, keys))
  }, [columns])

  const reset = useCallback(() => {
    _setOrder(_withStickyFirst(columns, columns.map((c) => c.key)))
    _setHiddenSet(new Set())
    _setSortKey(defaultSort?.key ?? null)
    _setSortDir(defaultSort?.dir ?? 'asc')
  }, [columns, defaultSort])

  const sortRows = useCallback(
    <Row,>(rows: Row[], valueFor: (row: Row, key: string) => unknown): Row[] => {
      if (!sortKey) return rows
      const dir = sortDir === 'asc' ? 1 : -1
      const arr = [...rows]
      arr.sort((a, b) => {
        const av = valueFor(a, sortKey)
        const bv = valueFor(b, sortKey)
        const aIsNull = av === null || av === undefined || av === ''
        const bIsNull = bv === null || bv === undefined || bv === ''
        if (aIsNull && bIsNull) return 0
        if (aIsNull) return 1
        if (bIsNull) return -1
        if (typeof av === 'number' && typeof bv === 'number') {
          return (av - bv) * dir
        }
        return String(av).localeCompare(String(bv)) * dir
      })
      return arr
    },
    [sortKey, sortDir],
  )

  return {
    columns,
    visibleColumns,
    hidden,
    sortKey,
    sortDir,
    toggleSort,
    setHidden,
    setOrder,
    reset,
    sortRows,
  }
}

/** Reorder helper — keep sticky columns at the front in their definition
 *  order; honor the desiredOrder for non-sticky columns; append any
 *  non-sticky columns that aren't in desiredOrder so they aren't lost. */
function _withStickyFirst(
  columns: ReadonlyArray<ColumnDef>,
  desiredOrder: string[],
): string[] {
  const sticky = columns.filter((c) => c.sticky).map((c) => c.key)
  const stickySet = new Set(sticky)
  const known = new Set(columns.map((c) => c.key))
  const seen = new Set<string>()
  const rest: string[] = []
  for (const k of desiredOrder) {
    if (stickySet.has(k) || !known.has(k) || seen.has(k)) continue
    seen.add(k)
    rest.push(k)
  }
  for (const c of columns) {
    if (c.sticky || seen.has(c.key)) continue
    rest.push(c.key)
  }
  return [...sticky, ...rest]
}
